import type { Query, QueryClient } from '@tanstack/react-query';
import type { PollRead } from '@models/api/poll';
import {
  getQuestionEntity,
  isQuestionListKey,
  isResolveQuestionKey,
  questionByUuidKey,
  questionDetailKey,
  questionEntityKey,
} from '@lib/cache/questions';

/** Deep enough for every question cache shape: infinite pages → page → data → items → question. */
const MAX_DEPTH = 6;

const isPlainObject = (value: unknown): value is Record<string, unknown> => {
  if (typeof value !== 'object' || value === null) return false;
  const proto: unknown = Object.getPrototypeOf(value);
  return proto === Object.prototype || proto === null;
};

/** A question-shaped record: the id, and a title as every question read carries. */
const isTheQuestion = (value: Record<string, unknown>, questionId: string): boolean =>
  value.id === questionId && typeof value.title === 'string';

/**
 * `value` with the poll block of every copy of `questionId` replaced, immutably: what is unchanged
 * keeps its identity, and `value` itself comes back when nothing in it is that question. Plain
 * objects and arrays only, so an axios response's request or headers objects are never walked.
 */
const replacePoll = (
  value: unknown,
  questionId: string,
  poll: PollRead,
  depth: number,
): unknown => {
  if (depth > MAX_DEPTH) return value;
  if (Array.isArray(value)) {
    const entries: readonly unknown[] = value;
    let next: unknown[] | null = null;
    for (let index = 0; index < entries.length; index += 1) {
      const entry = entries[index];
      const replaced = replacePoll(entry, questionId, poll, depth + 1);
      if (replaced !== entry) {
        next ??= [...entries];
        next[index] = replaced;
      }
    }
    return next ?? value;
  }
  if (!isPlainObject(value)) return value;
  if (isTheQuestion(value, questionId)) {
    return value.poll === poll ? value : { ...value, poll };
  }
  let next: Record<string, unknown> | null = null;
  for (const [key, entry] of Object.entries(value)) {
    const replaced = replacePoll(entry, questionId, poll, depth + 1);
    if (replaced !== entry) {
      next ??= { ...value };
      next[key] = replaced;
    }
  }
  return next ?? value;
};

/**
 * Writes a returned `PollRead` into every cached copy of its question (TECH-197 § Edge cases
 * "Cache": "update that poll in every cached list from the returned block; no refetch of whole
 * feeds"): the entity store every card renders from, every question list (feeds, the user's
 * posts), the id-keyed detail, the post page's reads by slug and by uuid. Used after a vote, an
 * end, a reopen (FE-1b, FE-1c) and the card's own re-read when its end time passes.
 *
 * Lists mostly hold ids, so the entity store is what surfaces show; the raw copies are written too
 * so a list or read that is handed back later never carries the old block. A copy that does not
 * hold the question is left alone (no write, its freshness kept), and a write keeps the query's
 * `dataUpdatedAt`, so nothing is marked fresh that was not read.
 */
export const writePollIntoCaches = (
  queryClient: QueryClient,
  questionId: string,
  poll: PollRead,
): void => {
  const entity = getQuestionEntity(queryClient, questionId);
  if (entity && entity.poll !== poll) {
    queryClient.setQueryData(questionEntityKey(questionId), { ...entity, poll });
  }

  const detailHash = JSON.stringify(questionDetailKey(questionId));
  const byUuidHash = JSON.stringify(questionByUuidKey(questionId));
  const holdsCopies = (query: Query): boolean => {
    const key = query.queryKey;
    if (isQuestionListKey(key) || isResolveQuestionKey(key)) return true;
    const hash = JSON.stringify(key);
    return hash === detailHash || hash === byUuidHash;
  };

  for (const query of queryClient.getQueryCache().findAll({ predicate: holdsCopies })) {
    const data: unknown = query.state.data;
    if (data === undefined) continue;
    const next = replacePoll(data, questionId, poll, 0);
    if (next !== data) {
      queryClient.setQueryData(query.queryKey, next, { updatedAt: query.state.dataUpdatedAt });
    }
  }
};
